import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  EventNote as EventNoteIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

const API_BASE = getApiUrl();

export type ChatSupportLead = {
  id: string;
  contact_id: string;
  support_type: string;
  status: string;
  priority: string;
  notes?: string | null;
  comment?: string | null;
  custom_fields?: Record<string, unknown>;
  created_at: string;
};

const SUPPORT_TYPE_LABEL: Record<string, string> = {
  technical_support: 'Suporte Técnico',
  billing_support: 'Suporte Billing',
  general_support: 'Suporte Geral',
  urgencia: 'Urgência',
  agendamento: 'Agendamento',
  remarcacao: 'Remarcação',
  cancelamento: 'Cancelamento',
  reclamacao: 'Reclamação',
  financeiro: 'Financeiro',
  assistencia_medica: 'Assistência Médica',
};

/** Tab na rota /support?tab=… (mesmas chaves do SupportDashboard) */
const SUPPORT_TAB_FOR_TYPE: Record<string, string> = {
  technical_support: 'technical_support',
  billing_support: 'billing_support',
  general_support: 'general_support',
  urgencia: 'urgencia',
  agendamento: 'agendamento',
  remarcacao: 'remarcacao',
  cancelamento: 'cancelamento',
  reclamacao: 'reclamacao',
  financeiro: 'financeiro',
  assistencia_medica: 'assistencia_medica',
};

const PRIORITY_LABEL: Record<string, string> = {
  urgent: 'Urgente',
  high: 'Alta',
  normal: 'Normal',
  low: 'Baixa',
};

interface Props {
  conversationId: string | null | undefined;
  /** Barra sob o cabeçalho da conversa — mais compacta, sem área de mensagens. */
  compact?: boolean;
}

/**
 * Mostra solicitações da Central de Atendimento (support_leads) ligadas à conversa atual
 * e permite marcar como resolvido sem sair do chat.
 */
const ChatSupportEventsBanner: React.FC<Props> = ({ conversationId, compact = false }) => {
  const theme = useTheme();
  const [leads, setLeads] = useState<ChatSupportLead[]>([]);
  const [loading, setLoading] = useState(false);
  const [resolveLead, setResolveLead] = useState<ChatSupportLead | null>(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!conversationId) {
      setLeads([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_BASE}/support/conversations/${conversationId}/open-leads`);
      if (data?.success && Array.isArray(data.data)) {
        setLeads(data.data);
      } else {
        setLeads([]);
      }
    } catch (e: unknown) {
      console.error('ChatSupportEventsBanner load', e);
      setLeads([]);
      setError('Não foi possível carregar eventos de atendimento.');
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openResolve = (lead: ChatSupportLead) => {
    setResolveLead(lead);
    setResolveNotes('');
  };

  const handleResolve = async () => {
    if (!resolveLead) return;
    const notes = resolveNotes.trim() || 'Resolvido no chat.';
    setResolving(true);
    setError(null);
    try {
      await axios.post(`${API_BASE}/support/leads/${resolveLead.id}/resolve`, { notes });
      setResolveLead(null);
      setResolveNotes('');
      await load();
    } catch (e: unknown) {
      console.error('resolve support lead', e);
      setError('Erro ao marcar como resolvido.');
    } finally {
      setResolving(false);
    }
  };

  if (!conversationId) return null;
  if (loading && leads.length === 0) return null;
  if (!loading && leads.length === 0 && !error) return null;

  return (
    <>
      <Box sx={{ width: '100%', flexShrink: 0, mb: compact ? 0 : 2 }}>
        {error && (
          <Alert severity="warning" sx={{ mb: compact ? 0.5 : 1, py: compact ? 0.25 : undefined }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {leads.map((lead, idx) => {
          const typeLabel = SUPPORT_TYPE_LABEL[lead.support_type] || lead.support_type;
          const tab = SUPPORT_TAB_FOR_TYPE[lead.support_type] || '';
          const centralHref = tab ? `/support?tab=${encodeURIComponent(tab)}` : '/support';
          const pri = PRIORITY_LABEL[lead.priority] || lead.priority;
          const previewRaw = (lead.notes || lead.comment || '').replace(/\s+/g, ' ');
          const preview = previewRaw.slice(0, compact ? 80 : 120);

          return (
            <Alert
              key={lead.id}
              severity="info"
              icon={<EventNoteIcon fontSize={compact ? 'small' : 'medium'} />}
              sx={{
                mb: compact ? (idx === leads.length - 1 ? 0 : 0.5) : 1,
                py: compact ? 0.35 : undefined,
                alignItems: 'flex-start',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.12)' : '#e3f2fd',
                border: `1px solid ${theme.palette.info.main}33`,
              }}
            >
              <Stack spacing={compact ? 0.5 : 1} sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                  {!compact && (
                    <Typography variant="subtitle2" component="span" sx={{ fontWeight: 700 }}>
                      Central de Atendimento
                    </Typography>
                  )}
                  <Chip size="small" label={typeLabel} color="primary" variant="outlined" sx={compact ? { height: 22, fontSize: '0.7rem' } : undefined} />
                  <Chip size="small" label={pri} variant="outlined" sx={compact ? { height: 22, fontSize: '0.7rem' } : undefined} />
                </Box>
                {preview ? (
                  <Typography variant={compact ? 'caption' : 'body2'} color="text.secondary" sx={compact ? { lineHeight: 1.35 } : undefined}>
                    {preview}
                    {previewRaw.length > (compact ? 80 : 120) ? '…' : ''}
                  </Typography>
                ) : null}
                <Box role="group" aria-label="Ações do suporte" sx={{ display: 'flex', flexWrap: 'wrap', gap: compact ? 0.5 : 1, alignItems: 'center' }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon sx={{ fontSize: compact ? 16 : 20 }} />}
                    onClick={() => openResolve(lead)}
                  >
                    {compact ? 'Resolver' : 'Marcar como resolvido'}
                  </Button>
                  <Link component={RouterLink} to={centralHref} underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: compact ? '0.75rem' : '0.875rem' }}>
                    Central
                    <OpenInNewIcon sx={{ fontSize: compact ? 14 : 16 }} />
                  </Link>
                </Box>
              </Stack>
            </Alert>
          );
        })}
      </Box>

      <Dialog open={!!resolveLead} onClose={() => !resolving && setResolveLead(null)} fullWidth maxWidth="sm">
        <DialogTitle>Marcar como resolvido</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {resolveLead ? (SUPPORT_TYPE_LABEL[resolveLead.support_type] || resolveLead.support_type) : ''}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Notas (obrigatório na API — pode ser breve)"
            fullWidth
            multiline
            minRows={2}
            value={resolveNotes}
            onChange={(e) => setResolveNotes(e.target.value)}
            placeholder="Ex.: Resolvido no WhatsApp — paciente confirmou."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveLead(null)} disabled={resolving}>
            Cancelar
          </Button>
          <Button variant="contained" color="success" onClick={() => void handleResolve()} disabled={resolving}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChatSupportEventsBanner;
